/**
 * Egyetlen bruttó (s DPH) költésszabály minden nézethez.
 * Minden fizető beleszámít. A fázis nélküli kiadás nem esik ki, hanem
 * a „Fázishoz nem rendelt” vödörbe kerül.
 *
 * A `v_phase_financials` nézet csak a feladathoz kötött kiadást látja
 * (`expenses.task_id`). A fázissal rendelkező, de feladat nélküli sor
 * (élőben: Alapozás villámhárító 220 + 65) és a fázis nélküli sor emiatt
 * hiányzott a Büdzsé „Tény” oszlopából.
 */

export const UNASSIGNED_PHASE_KEY = "__unassigned__";
export const UNASSIGNED_PHASE_LABEL = "Fázishoz nem rendelt";
export const UNASSIGNED_TASK_LABEL = "Feladathoz nem rendelt";

export type SpendExpense = {
  amount?: number | null;
  amount_gross?: number | null;
  phase_id?: string | null;
  task_id?: string | null;
  paid_by?: string | null;
};

/** Bruttó összeg. Ha `amount_gross` megvan, az a mérvadó; különben a kanonikus `amount` (szintén bruttó). */
export function expenseGrossSpend(row: {
  amount?: number | null | undefined;
  amount_gross?: number | null | undefined;
}): number {
  const gross = row.amount_gross;
  if (typeof gross === "number" && Number.isFinite(gross)) return gross;
  const amount = row.amount;
  if (typeof amount === "number" && Number.isFinite(amount)) return amount;
  return 0;
}

/** A keret a `project.budget_total` mező. Hiányzó adatnál nincs beégetett összeg. */
export function projectBudgetTotal(
  project: { budget_total?: number | null } | null | undefined
): number | null {
  const value = project?.budget_total;
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function totalGrossSpend(expenses: readonly SpendExpense[]): number {
  return expenses.reduce((sum, row) => sum + expenseGrossSpend(row), 0);
}

export function attributePhaseId(
  row: SpendExpense,
  taskPhaseById: ReadonlyMap<string, string | null | undefined>,
  knownPhaseIds?: ReadonlySet<string>
): string | null {
  const direct = row.phase_id ?? null;
  if (direct && (!knownPhaseIds || knownPhaseIds.has(direct))) return direct;
  const viaTask = row.task_id ? (taskPhaseById.get(row.task_id) ?? null) : null;
  if (viaTask && (!knownPhaseIds || knownPhaseIds.has(viaTask))) return viaTask;
  return null;
}

export function sumGrossSpendByPhase(
  expenses: readonly SpendExpense[],
  taskPhaseById: ReadonlyMap<string, string | null | undefined> = new Map(),
  knownPhaseIds?: ReadonlySet<string>
): { byPhase: Map<string, number>; unassigned: number; total: number } {
  const byPhase = new Map<string, number>();
  let unassigned = 0;
  for (const row of expenses) {
    const amount = expenseGrossSpend(row);
    if (!amount) continue;
    const phaseId = attributePhaseId(row, taskPhaseById, knownPhaseIds);
    if (!phaseId) {
      unassigned += amount;
      continue;
    }
    byPhase.set(phaseId, (byPhase.get(phaseId) ?? 0) + amount);
  }
  let assigned = 0;
  for (const value of byPhase.values()) assigned += value;
  return { byPhase, unassigned, total: assigned + unassigned };
}

export type PhaseSpendRow = {
  phase_id: string;
  title: string;
  order_index: number;
  planned_gross: number;
  committed_gross: number;
  actual_gross: number;
  estimate_to_complete: number;
  eac_gross: number;
  variance_pct: number | null;
};

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * A nézet „Tény” oszlopát a közös bruttó költés váltja fel.
 * Az EAC a maradványképlet szerint mozdul: a többlet tény először a
 * becsült hátralévőt fogyasztja, utána növeli az EAC-t.
 */
export function reconcilePhaseActuals<T extends PhaseSpendRow>(
  rows: readonly T[],
  spend: { byPhase: Map<string, number>; unassigned: number }
): { rows: T[]; unassignedRow: T | null } {
  const next = rows.map((row) => {
    const actual = spend.byPhase.get(row.phase_id) ?? 0;
    const delta = actual - Number(row.actual_gross);
    const committed = Number(row.committed_gross) || 0;
    const etc = Math.max(0, (Number(row.estimate_to_complete) || 0) - delta);
    const eac = actual + committed + etc;
    const planned = Number(row.planned_gross) || 0;
    return {
      ...row,
      actual_gross: actual,
      estimate_to_complete: etc,
      eac_gross: eac,
      variance_pct: planned > 0 ? round1((eac / planned - 1) * 100) : null
    };
  });
  const unassignedRow =
    spend.unassigned > 0
      ? ({
          phase_id: UNASSIGNED_PHASE_KEY,
          title: UNASSIGNED_PHASE_LABEL,
          order_index: 10_000,
          planned_gross: 0,
          committed_gross: 0,
          actual_gross: spend.unassigned,
          estimate_to_complete: 0,
          eac_gross: spend.unassigned,
          variance_pct: null
        } as T)
      : null;
  return { rows: next, unassignedRow };
}
